// Utilities for numeric formatting in Local File export

function getThousandsSeparator(decimalDelimiter) {
  return decimalDelimiter === '.' ? ',' : '.';
}

function clampInt(value, min, max, fallback) {
  const n = Number.isFinite(value) ? Math.floor(value) : fallback;
  return Math.max(min, Math.min(max, n));
}

// Attempts to auto-detect the decimal/thousands separators from the input string.
// Rules:
// - If both '.' and ',' exist, the rightmost one is treated as decimal; the other as thousands.
// - If only one of them exists, treat it as thousands if it creates consistent 3-digit groups; otherwise as decimal.
// - Whitespace and apostrophes are ignored; leading '-' is supported.
function safeToNumber(raw, { decimalDelimiter } = {}) {
  if (raw === null || raw === undefined) return NaN;
  if (typeof raw === 'number') return raw;
  if (typeof raw !== 'string') return NaN;
  let s = raw.trim();
  if (!s) return NaN;

  // Normalize whitespace and remove currency/symbols (keep digits, separators, and sign)
  let neg = false;
  if (s.startsWith('-')) {
    neg = true;
    s = s.slice(1);
  } else if (s.startsWith('+')) {
    s = s.slice(1);
  }
  s = s.replace(/\s+/g, '');
  // Remove any character except digits, separators, and minus
  s = s.replace(/[^0-9.,-]/g, '');
  s = s.replace(/'/g, ''); // Swiss-style thousands

  // Now s contains only digits and separators
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let decimalSep = null;
  let thousandSep = null;

  if (lastDot !== -1 && lastComma !== -1) {
    // Both present: rightmost is decimal
    if (lastDot > lastComma) {
      decimalSep = '.';
      thousandSep = ',';
    } else {
      decimalSep = ',';
      thousandSep = '.';
    }
  } else if (lastComma !== -1) {
    // Only comma present
    const parts = s.split(',');
    const isAllDigits = parts.every((p) => /^\d+$/.test(p));
    const firstLen = parts[0].length;
    const lastLen = parts[parts.length - 1].length;
    const middleOk = parts.length > 2 ? parts.slice(1, -1).every((p) => p.length === 3) : true;
    const thousandPattern =
      isAllDigits && middleOk && lastLen === 3 && firstLen >= 1 && firstLen <= 3;
    if (thousandPattern) {
      thousandSep = ',';
    } else {
      decimalSep = ',';
    }
  } else if (lastDot !== -1) {
    // Only dot present
    const parts = s.split('.');
    const isAllDigits = parts.every((p) => /^\d+$/.test(p));
    const firstLen = parts[0].length;
    const lastLen = parts[parts.length - 1].length;
    const middleOk = parts.length > 2 ? parts.slice(1, -1).every((p) => p.length === 3) : true;
    const thousandPattern =
      isAllDigits && middleOk && lastLen === 3 && firstLen >= 1 && firstLen <= 3;
    if (thousandPattern) {
      thousandSep = '.';
    } else {
      decimalSep = '.';
    }
  } else {
    // No separators
  }

  // As an optional fallback, if undecided and a preferred delimiter was provided, apply it
  if (!decimalSep && !thousandSep && (decimalDelimiter === ',' || decimalDelimiter === '.')) {
    // Prefer treating provided delimiter as decimal when present in string
    if (s.includes(decimalDelimiter)) {
      decimalSep = decimalDelimiter;
    }
  }

  let normalized = s;
  if (thousandSep) {
    const re = new RegExp('\\' + thousandSep, 'g');
    normalized = normalized.replace(re, '');
  }
  if (decimalSep) {
    const re = new RegExp('\\' + decimalSep, 'g');
    // Replace only the last occurrence to preserve cases where both exist? Since we normalized above, all should be the same.
    normalized = normalized.replace(re, '.');
  }

  // Ensure only one leading minus
  normalized = (neg ? '-' : '') + normalized.replace(/-/g, '');

  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : NaN;
}

function formatWithSeparators(numberValue, decimals, decimalDelimiter) {
  if (!Number.isFinite(numberValue)) return '';
  const fixed = Number(numberValue).toFixed(decimals);
  const thousands = getThousandsSeparator(decimalDelimiter);
  const [intPartRaw, decPart = ''] = fixed.split('.');
  const isNegative = intPartRaw.startsWith('-');
  const intPart = isNegative ? intPartRaw.slice(1) : intPartRaw;
  const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const signed = isNegative ? `-${intWithSep}` : intWithSep;
  return decPart ? `${signed}${decimalDelimiter}${decPart}` : signed;
}

export function formatAmount(value, { decimals = 2, decimalDelimiter = '.' } = {}) {
  const decs = clampInt(decimals, 0, 6, 2);
  const num = safeToNumber(value, { decimalDelimiter });
  if (!Number.isFinite(num)) return '';
  return formatWithSeparators(num, decs, decimalDelimiter);
}

export function formatPercent(
  value,
  { decimals = 2, decimalDelimiter = '.', appendSymbol = false } = {}
) {
  const decs = clampInt(decimals, 0, 6, 2);
  const num = safeToNumber(value, { decimalDelimiter });
  if (!Number.isFinite(num)) return '';
  const core = formatWithSeparators(num, decs, decimalDelimiter);
  return appendSymbol ? `${core}%` : core;
}

export function inferValueType(field) {
  const vt = field && field.valueType;
  if (vt === 'percent' || vt === 'amount') return vt;

  const topic = (field && field.topic) || '';
  const formula = (field && field.formula) || '';
  const topicLower = String(topic).toLowerCase();

  if (topic.includes('%')) return 'percent';
  if (topicLower.includes('percent')) return 'percent';
  if (topicLower.includes('margin')) return 'percent';
  if (topicLower.includes('rate')) return 'percent';
  if (String(formula).includes('*100')) return 'percent';
  return 'amount';
}

export function formatByType(value, field, opts = {}) {
  const type = inferValueType(field);
  if (type === 'percent') {
    const percentOpts = opts.percent || opts;
    return formatPercent(value, percentOpts);
  }
  const amountOpts = opts.amount || opts;
  return formatAmount(value, amountOpts);
}
